import React, { useState } from 'react';
import {
  ListItem,
  IconButton,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  MoreVert,
  Delete,
  CompareArrows,
  PictureInPicture,
  DragIndicator,
} from '@mui/icons-material';

import MovePlayerDialog from './MovePlayerDialog';
import DeletePlayerDialog from './DeletePlayerDialog';
import { Player, Position, TeamName } from '../types';
import PositionDialog from './PositionDialog';
import styled from '@emotion/styled';
import { PlayerAvatar } from './PlayerAvatar';

const Wrapper = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<Partial<PlayerItemProps>>(({ isDragging }) => {
  return {
    background: isDragging ? '#e6ee9c' : 'inherit',
    ':hover': {
      svg: {
        visibility: 'visible',
      },
      // Add shadow on top and bottom
      boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.1)',
    },
  };
});

type PlayerItemProps = {
  player: Player;
  canMoveTo: TeamName[];
  onDelete?: (playerName: string) => void;
  onMove?: (playerName: string, toTeam: TeamName) => void;
  onSetPosition?: (player: Player, position?: Position) => void;
  dragHandleProps?: any;
  isDragging?: boolean;
  hidePosition?: boolean;
};

export default function PlayerItem({
  player,
  canMoveTo,
  onDelete,
  onMove,
  onSetPosition,
  dragHandleProps,
  isDragging,
  hidePosition,
}: Readonly<PlayerItemProps>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPositionDialog, setShowPositionDialog] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
  };

  const handleDeleteClick = () => {
    handleClose();
    setShowDeleteDialog(true);
  };

  const handleDelete = () => {
    handleClose();
    onDelete && onDelete(player.name);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowMoveDialog(false);
  };

  const handleMove = () => {
    handleClose();
    setShowMoveDialog(true);
  };

  const handleMoveDlgClose = (team?: string) => {
    setShowMoveDialog(false);
    team && onMove && onMove(player.name, team as TeamName);
  };

  const handlePosition = () => {
    handleClose();
    setShowPositionDialog(true);
  };

  const handlePositionDialogClose = (position?: Position) => {
    setShowPositionDialog(false);
    onSetPosition && onSetPosition(player, position);
  };

  let playerText = player.name;

  if (player.position && !hidePosition) {
    playerText += ` - ${player.position}`;
  }

  return (
    <Wrapper
      disableGutters
      isDragging={isDragging}
      secondaryAction={
        <IconButton onClick={handleClick}>
          <MoreVert />
        </IconButton>
      }
      {...dragHandleProps}
    >
      <DragIndicator
        color={isDragging ? 'primary' : 'disabled'}
        visibility={isDragging ? 'visible' : 'hidden'}
      />
      <ListItemAvatar>
        <PlayerAvatar name={player.name} />
      </ListItemAvatar>
      <ListItemText primary={playerText} />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMove}>
          <ListItemIcon>
            <CompareArrows fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePosition}>
          <ListItemIcon>
            <PictureInPicture fontSize="small" />
          </ListItemIcon>
          <ListItemText>Position</ListItemText>
        </MenuItem>
      </Menu>
      <MovePlayerDialog
        open={showMoveDialog}
        toTeams={canMoveTo}
        onClose={handleMoveDlgClose}
      />
      <DeletePlayerDialog
        open={showDeleteDialog}
        player={player.name}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
      />
      <PositionDialog
        open={showPositionDialog}
        onClose={handlePositionDialogClose}
      />
    </Wrapper>
  );
}
